<template>
  <div class="px-4 md:px-8 py-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-[22px] md:text-[24px] font-semibold text-bodyText">
          Create Article
        </h1>
        <p class="text-sm text-grayDark">
          Write and publish an article for the community.
        </p>
      </div>
    </div>

    <div
      class="bg-white border border-gray-100 rounded-[12px] shadow-sm max-w-4xl mx-auto"
    >
      <form @submit.prevent="submit">
        <!-- form header status/message -->
        <div
          v-if="message"
          :class="[
            'px-6 py-3 rounded-t-[12px]',
            messageClass.includes('green') ? 'bg-green-50' : 'bg-red-50',
          ]"
        >
          <p :class="messageClass">{{ message }}</p>
        </div>

        <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- Title -->
          <div class="md:col-span-2">
            <label class="flex items-center gap-1 text-sm text-gray-600 mb-1">
              Title <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              v-model.trim="form.title"
              :maxlength="TITLE_MAX"
              :class="[
                'w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30',
                errors.title ? 'border-red-300' : 'border-gray-200',
              ]"
              aria-required="true"
              :aria-invalid="!!errors.title"
            />
            <div class="mt-1 flex items-center justify-between">
              <p v-if="errors.title" class="text-xs text-red-500">
                {{ errors.title }}
              </p>
              <p class="text-xs text-gray-400 ml-auto">
                {{ titleCharsLeft }} chars left
              </p>
            </div>
          </div>

          <!-- Subtitle -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Subtitle</label>
            <input
              v-model.trim="form.subtitle"
              class="w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30"
            />
          </div>

          <!-- Category -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Category</label>
            <select
              v-model.number="form.category_id"
              class="w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30"
            >
              <option :value="null">Select category</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">
                {{ c.name }}
              </option>
            </select>
          </div>

          <!-- Content -->
          <div class="md:col-span-2">
            <label class="flex items-center gap-1 text-sm text-gray-600 mb-1">
              Content <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            <textarea
              v-model.trim="form.content"
              rows="12"
              :class="[
                'w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30',
                errors.content ? 'border-red-300' : 'border-gray-200',
              ]"
              aria-required="true"
              :aria-invalid="!!errors.content"
              placeholder="Write your article in Markdown or plain text"
            ></textarea>
            <div class="mt-1">
              <p v-if="errors.content" class="text-xs text-red-500">
                {{ errors.content }}
              </p>
            </div>
          </div>

          <!-- Excerpt -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Excerpt</label>
            <textarea
              v-model.trim="form.excerpt"
              :maxlength="EXCERPT_MAX"
              rows="3"
              class="w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30"
              placeholder="Short summary (used in lists)"
            ></textarea>
            <div class="mt-1 text-xs text-gray-400 text-right">
              {{ excerptCharsLeft }} chars left
            </div>
          </div>

          <!-- Tags -->
          <div>
            <label class="block text-sm text-gray-600 mb-1"
              >Tags (comma separated)</label
            >
            <input
              v-model="tagsInput"
              class="w-full border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primaryDarkBg/30"
              placeholder="e.g. product, design"
            />
            <div v-if="parsedTags.length" class="mt-2 flex flex-wrap gap-2">
              <span
                v-for="t in parsedTags"
                :key="t"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primaryDarkBg/10 text-primaryDarkBg"
                >#{{ t }}</span
              >
            </div>
          </div>

          <!-- Featured Image Upload -->
          <div class="md:col-span-2">
            <label class="block text-sm text-gray-600 mb-1"
              >Featured image (optional)</label
            >
            <div
              class="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-center cursor-pointer hover:border-primaryDarkBg/40 transition"
              @click="openFilePicker"
              @dragover.prevent
              @drop.prevent="onDropFile"
            >
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onFeaturedImageChange"
              />
              <div v-if="!featuredImagePreview" class="text-gray-500 text-sm">
                <p>Click to upload, or drag and drop</p>
                <p class="text-xs text-gray-400">PNG, JPG up to ~5MB</p>
              </div>
              <div v-else class="w-full">
                <div class="flex items-center justify-between mb-2">
                  <p class="text-sm text-gray-600">Selected image preview</p>
                  <button
                    type="button"
                    @click.stop="removeFeaturedImage"
                    class="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <img
                  :src="featuredImagePreview"
                  alt="Featured preview"
                  class="w-full h-52 object-cover rounded-md"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 pb-6 flex items-center gap-3">
          <button
            :disabled="submitting"
            type="submit"
            class="px-4 py-2 bg-primaryDarkBg text-primaryLight rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {{ submitting ? "Saving..." : "Publish" }}
          </button>
          <button
            type="button"
            @click="saveDraft"
            :disabled="submitting"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Save Draft
          </button>
          <span class="text-xs text-gray-400"
            >Required fields are marked with *</span
          >
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";

export default {
  name: "CreateArticleComponent",
  data() {
    return {
      form: {
        title: "",
        subtitle: "",
        content: "",
        excerpt: "",
        category_id: null,
      },
      tagsInput: "",
      categories: [],
      submitting: false,
      message: "",
      errors: {},
      // featured image handling
      featuredImageFile: null,
      featuredImagePreview: null,
      // ui limits
      TITLE_MAX: 120,
      EXCERPT_MAX: 200,
    };
  },
  computed: {
    messageClass() {
      return this.message && this.message.includes("success")
        ? "text-green-600"
        : "text-red-600";
    },
    parsedTags() {
      if (!this.tagsInput) return [];
      return this.tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    },
    titleCharsLeft() {
      return Math.max(0, this.TITLE_MAX - (this.form.title?.length || 0));
    },
    excerptCharsLeft() {
      return Math.max(0, this.EXCERPT_MAX - (this.form.excerpt?.length || 0));
    },
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await apiClient.get("/web/categories");
        const data = res && res.data && res.data.data ? res.data.data : [];
        this.categories = data;
      } catch (e) {
        console.error("Error fetching categories", e);
      }
    },

    validate() {
      const errs = {};
      if (!this.form.title?.trim()) errs.title = "Title is required";
      if (!this.form.content?.trim()) errs.content = "Content is required";
      this.errors = errs;
      return Object.keys(errs).length === 0;
    },

    openFilePicker() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },

    onFeaturedImageChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.featuredImageFile = file;
      const reader = new FileReader();
      reader.onload = (evt) => {
        this.featuredImagePreview = evt.target?.result;
      };
      reader.readAsDataURL(file);
    },

    onDropFile(e) {
      const file =
        e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!file) return;
      this.featuredImageFile = file;
      const reader = new FileReader();
      reader.onload = (evt) => {
        this.featuredImagePreview = evt.target?.result;
      };
      reader.readAsDataURL(file);
    },

    removeFeaturedImage() {
      this.featuredImageFile = null;
      this.featuredImagePreview = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async submit() {
      if (!this.validate()) return;
      this.submitting = true;
      this.message = "";
      try {
        const fd = new FormData();

        fd.append("title", this.form.title.trim());
        fd.append("content", this.form.content.trim());
        if (this.form.subtitle)
          fd.append("subtitle", this.form.subtitle.trim());
        if (this.form.excerpt) fd.append("excerpt", this.form.excerpt.trim());
        if (
          this.form.category_id !== null &&
          this.form.category_id !== undefined
        )
          fd.append("category_id", String(this.form.category_id));
        if (this.parsedTags.length)
          fd.append("tags", this.parsedTags.join(","));
        fd.append("article_status", "published");
        if (this.featuredImageFile)
          fd.append(
            "featured_image",
            this.featuredImageFile,
            this.featuredImageFile.name
          );

        console.log("Preparing form data for submission...");
        for (const [key, value] of fd.entries()) {
          console.log(`${key}:`, value);
        }

        const config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };

        const res = await apiClient.post("/web/articles", fd, config);
        if (res && res.data && res.data.success) {
          this.message = "Article created successfully";
          this.$router.push({ name: "NewsInsight" });
        } else {
          this.message = "Failed to create article";
        }
      } catch (e) {
        console.error("Create article error", e);
        this.message =
          (e.response && e.response.data && e.response.data.message) ||
          "An error occurred";
      } finally {
        this.submitting = false;
      }
    },

    async saveDraft() {
      if (!this.validate()) return;
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append("title", this.form.title.trim());
        fd.append("content", this.form.content.trim());
        if (this.form.subtitle)
          fd.append("subtitle", this.form.subtitle.trim());
        if (this.form.excerpt) fd.append("excerpt", this.form.excerpt.trim());
        if (
          this.form.category_id !== null &&
          this.form.category_id !== undefined
        )
          fd.append("category_id", String(this.form.category_id));
        if (this.parsedTags.length)
          fd.append("tags", this.parsedTags.join(","));
        fd.append("article_status", "draft");
        if (this.featuredImageFile)
          fd.append(
            "featured_image",
            this.featuredImageFile,
            this.featuredImageFile.name
          );

        console.log("Form data entries:");
        for (const [key, value] of fd.entries()) {
          console.log(`${key}:`, value);
        }

        const config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
        const res = await apiClient.post("/web/articles", fd, config);
        if (res && res.data && res.data.success) {
          this.message = "Draft saved successfully";
          this.$router.push({ name: "NewsInsight" });
        } else {
          this.message = "Failed to save draft";
        }
      } catch (e) {
        console.error("Save draft error", e);
        this.message =
          (e.response && e.response.data && e.response.data.message) ||
          "An error occurred";
      } finally {
        this.submitting = false;
      }
    },
  },
  created() {
    this.fetchCategories();
  },
};
</script>

<style scoped>
.text-green-600 {
  color: #16a34a;
}
.text-red-600 {
  color: #dc2626;
}
</style>
